import * as React from 'react';
import * as cx from 'classnames';
import SuperCritical from 'lib/supercritical';
import { noDefault } from 'lib/eventWrappers';

import { CoordinationEditingPolicy } from 'server/enums/CoordinationEditingPolicy';
import { CoordinationProviderCurrencys, CoordinationProviderCurrencyDisplayName } from 'server/enums/CoordinationProviderCurrency';

import {
    AddLocationViewModel,
    CCardStatusViewModel,
    CoordinationConfigurator,
    LocationDisplayKey,
    LocationViewModel,
    MilestoneTypeViewModel,
    ProviderViewModel
} from './model';

import { CCardStatusIndicator } from 'portcall/ccardstatusindicator/view';
import { PageLoadingIndicator } from 'core/actionloadingindicator/view';
import { PCSortable } from 'portcall/pcsortable/view';
import { TypeaheadInput } from 'portcall/typeahead/view';

export const CoordinationConfiguratorView = SuperCritical(({ model: m } : { model : CoordinationConfigurator }) =>
    <div className="coordinationconfigurator pc-page-fixedwidth">
        <div className="header-row">
            <Header model={m} />
            <SaveAndQuit model={m} />
        </div>
        <div className="page-body">
            <Title model={m} />
            <Description model={m} />
            <AdminNotes model={m} />
            <IsMandatory model={m} />
            <Functions model={m} />
            <AltStatusLabels model={m} />
            <MilestoneTypes model={m} />
            <MainLocations model={m} />
            <RelatedLocations model={m} />
            <Providers model={m} />
        </div>
        <PageLoadingIndicator loading={() => m.Loading() || m.SavingChanges()} />
    </div>
);

const Header = SuperCritical(({ model: m } : { model : CoordinationConfigurator }) =>
    <div className="header">
        Coordination Configurator<span className="slash">/</span>{m.Model.Title() || '[Card Title]'}
    </div>
);

const SaveAndQuit = SuperCritical(({ model: m } : { model : CoordinationConfigurator }) =>
    <div className="save-quit">
        <div>
            <button
                className="pc-button save-button"
                disabled={!m.HasUnsavedChanges() || m.SavingChanges() || m.ClientValidationErrorsCount() > 0}
                onClick={noDefault(m.SaveChanges)}
            >
                Save Changes
            </button>
            <a href="" onClick={noDefault(m.QuitChanges)}>Cancel</a>
        </div>
        {m.ClientValidationErrorsCount() > 0 &&
            <div className="config-error">
                {m.ClientValidationErrorsCount()} validation error{m.ClientValidationErrorsCount() === 1 ? '' : 's'} preventing save...
            </div>
        }
    </div>
);

const Title = SuperCritical(({ model: m } : { model : CoordinationConfigurator }) =>
    <div className="config-row title-row">
        <label className="config-row-label">Title</label>
        <div className="config-row-block">
            <div>
                <input
                    className="title-input pc-breaker-label"
                    placeholder="Text input"
                    value={m.Model.Title()}
                    onChange={e => m.Model.Title(e.currentTarget.value)}
                />
            </div>
            {m.ServerValidation()['Title'] &&
                <div className="config-error">{m.ServerValidation()['Title'].ErrorText}</div>
            }
        </div>
    </div>
);

const Description = SuperCritical(({ model: m } : { model : CoordinationConfigurator }) =>
    <div className="config-row descr-row">
        <label className="config-row-label">Description</label>
        <div className="config-row-block">
            <div>
                <input
                    className="descr-input pc-breaker-label"
                    placeholder="Text input"
                    value={m.Model.Description()}
                    onChange={e => m.Model.Description(e.currentTarget.value)}
                />
            </div>
            {m.ServerValidation()['Description'] &&
                <div className="config-error">{m.ServerValidation()['Description'].ErrorText}</div>
            }
        </div>
    </div>
);

const AdminNotes = SuperCritical(({ model: m } : { model : CoordinationConfigurator }) =>
    <div className="config-row admin-notes-row">
        <label className="config-row-label">Admin Notes</label>
        <div className="config-row-block">
            <div>
                <input
                    className="admin-notes-input pc-breaker-label"
                    placeholder="Text input"
                    value={m.Model.AdminNotes() ?? ''}
                    onChange={e => m.Model.AdminNotes(e.currentTarget.value || null)}
                />
            </div>
            {m.ServerValidation()['AdminNotes'] &&
                <div className="config-error">{m.ServerValidation()['AdminNotes'].ErrorText}</div>
            }
            <div className="config-descr admin-notes-descr">
                Optional notes that will only appear on the Coordination Configurator.
                You can use these to help distinguish between coordinations that might have the same Title but
                different Main/Related Locations.
            </div>
        </div>
    </div>
);

const IsMandatory = SuperCritical(({ model: m } : { model : CoordinationConfigurator }) =>
    <div className="config-row is-mandatory-row">
        <label className="config-row-label">Type</label>
        <div className="config-row-block">
            <div>
                <label className="pc-breaker">
                    <span className="pc-breaker-button">
                        <input
                            className="pc-breaker-input"
                            type="radio"
                            checked={m.Model.IsMandatory()}
                            onChange={e => m.Model.IsMandatory(e.currentTarget.checked)}
                        />
                        <span className="pc-breaker-light" />
                    </span>
                    <span className="pc-breaker-label">Mandatory: add to all PortCalls</span>
                </label>
            </div>
            <div>
                <label className="pc-breaker">
                    <span className="pc-breaker-button">
                        <input
                            className="pc-breaker-input"
                            type="radio"
                            checked={!m.Model.IsMandatory()}
                            onChange={e => m.Model.IsMandatory(!e.currentTarget.checked)}
                        />
                        <span className="pc-breaker-light" />
                    </span>
                    <span className="pc-breaker-label">Question: ask user if coordination is required</span>
                </label>
            </div>
            {!m.Model.IsMandatory() &&
                <div className="agent-prompt-shell">
                    <input
                        className="agent-prompt-input"
                        placeholder="Question Text"
                        value={m.Model.AgentPrompt() ?? ''}
                        onChange={e => m.Model.AgentPrompt(e.currentTarget.value)}
                    />
                    {m.ServerValidation()['AgentPrompt'] &&
                        <div className="config-error">{m.ServerValidation()['AgentPrompt'].ErrorText}</div>
                    }
                    <span className="config-descr agent-prompt-descr">
                        Enter the question the user needs to answer. Shorter is better. Example: Is pilotage required?
                    </span>
                </div>
            }
        </div>
    </div>
);

const Functions = SuperCritical(({ model: m } : { model : CoordinationConfigurator }) =>
    <div className="config-row functions-row">
        <label className="config-row-label">Functions</label>
        <div className="config-row-block">
            <div>
                <label className="pc-breaker">
                    <span className="pc-breaker-button">
                        <input
                            className="pc-breaker-input"
                            type="checkbox"
                            checked={m.Model.AppearsOnTraffic()}
                            onChange={e => m.Model.AppearsOnTraffic(e.currentTarget.checked)}
                        />
                        <span className="pc-breaker-light" />
                    </span>
                    <span className="pc-breaker-label">Appears on Traffic</span>
                </label>
            </div>
        </div>
    </div>
);

const AltStatusLabels = SuperCritical(({ model: m } : { model : CoordinationConfigurator }) =>
    <div className="config-row alt-status-labels-row">
        <label className="config-row-label">Status Labels</label>
        <div className="config-row-block">
            <div className="alt-status-labels-header">
                <span className="config-descr alt-status-labels-col col-definition">Default Definition/Label</span>
                <span className="config-descr alt-status-labels-col col-alt-label">Alternate Label</span>
            </div>
            <div className="alt-status-labels-body">
                {m.CoordinationCardStatuses().map(status =>
                    <AltStatusLabelRow status={status} key={status.Model.Id()} />
                )}
            </div>
        </div>
    </div>
);

const AltStatusLabelRow = SuperCritical(({ status: s } : { status : CCardStatusViewModel }) =>
    <div className="status-row">
        <div>
            <span className="alt-status-labels-col col-definition">
                <CCardStatusIndicator statusId={s.Model.Id()} />
                <span className="definition-text">{s.Model.Definition()}</span>
            </span>
            <span className="alt-status-labels-col col-alt-label">
                <input
                    className="alt-label-input"
                    value={s.AltLabelText() || (s.IsEditingLabel() ? '' : '--')}
                    onChange={e => s.AltLabelText(e.currentTarget.value)}
                    onFocus={() => s.IsEditingLabel(true)}
                    onBlur={() => s.IsEditingLabel(false)}
                />
            </span>
        </div>
        {s.ServerValidation()['Label'] &&
            <div className="config-error">{s.ServerValidation()['Label'].ErrorText}</div>
        }
    </div>
);

const MilestoneTypes = SuperCritical(({ model: m } : { model : CoordinationConfigurator }) =>
    <div className="config-row milestone-types-row">
        <label className="config-row-label">Milestones</label>
        <div className="config-row-block">
            <div>
                {m.MilestoneTypes().map(type =>
                    <MilestoneTypeRow type={type} model={m} key={type.Model.Id()} />
                )}
            </div>
            <div className="config-descr milestone-types-descr">
                Select all applicable milestones for this coordination.
            </div>
            {m.NumMilestoneTypesSelected() === 0 &&
                <div className="config-error">
                    Caution: There are no milestones selected. You may save this coordination, but it won't be
                    available for any PortCalls.
                </div>
            }
        </div>
    </div>
);

const MilestoneTypeRow = SuperCritical(({ type: t, model: m } : { type : MilestoneTypeViewModel, model : CoordinationConfigurator }) =>
    <div className="type-row">
        <label className="pc-breaker">
            <span className="pc-breaker-button">
                <input
                    className="pc-breaker-input"
                    type="checkbox"
                    checked={t.IsSelected()}
                    onChange={e => t.IsSelected(e.currentTarget.checked)}
                />
                <span className="pc-breaker-light" />
            </span>
            <span className="pc-breaker-label">{t.Model.Name()}</span>
        </label>
    </div>
);

const MainLocations = SuperCritical(({ model: m } : { model : CoordinationConfigurator }) =>
    <div className="config-row locations-row main-locations-row">
        <label className="config-row-label">Main Locations</label>
        <div className="config-row-block">
            {m.Model.IsScopedByMainLocation() ?
                <ExplicitLocations
                    locations={m.MainLocations}
                    addLocation={m.AddMainLocation}
                    onApplyToAll={() => m.Model.IsScopedByMainLocation(false)}
                /> :
                <AllLocations onSelectLocations={() => m.Model.IsScopedByMainLocation(true)} />
            }
            <div className="config-descr main-locations-descr">
                The main locations for this coordination. A milestone's main location is always the location
                where that milestone is occurring, which may be a berth or may be the sea point and/or pilot
                area.
            </div>
        </div>
    </div>
);

const RelatedLocations = SuperCritical(({ model: m } : { model : CoordinationConfigurator }) =>
    <div className="config-row locations-row related-locations-row">
        <label className="config-row-label">Related Locations</label>
        <div className="config-row-block">
            {m.Model.IsScopedByRelatedLocation() ?
                <ExplicitLocations
                    locations={m.RelatedLocations}
                    addLocation={m.AddRelatedLocation}
                    onApplyToAll={() => m.Model.IsScopedByRelatedLocation(false)}
                /> :
                <AllLocations onSelectLocations={() => m.Model.IsScopedByRelatedLocation(true)} />
            }
            <div className="config-descr related-locations-descr">
                The related locations for this coordination. For milestones at a movement's departure point, the
                related location is the movement's arrival point. For milestones at a movement's arrival point,
                the related location is the movement's departure point.
            </div>
        </div>
    </div>
);

const ExplicitLocations = SuperCritical(({
    locations,
    addLocation,
    onApplyToAll
} : {
    locations : () => LocationViewModel[],
    addLocation : AddLocationViewModel,
    onApplyToAll : () => void
}) =>
    <div>
        <div className="locations-input-shell">
            <TypeaheadInput
                className="pc-breaker-label add-location"
                model={addLocation.LocationSelector}
                placeholder="Add a location..."
            />
            <a
                href=""
                className="locations-scope-toggle"
                onClick={noDefault(onApplyToAll)}
            >
                Apply to All Locations
            </a>
        </div>
        {locations().length ?
            <div className="locations-selected-locations">
                {locations().map(l =>
                    // Keying by LocationId here as we expect the provided locations to already be filtered by
                    // Type.
                    <SelectedLocation location={l} key={l.Model.LocationId()} />
                )}
            </div> :
            <div className="config-error">
                Caution: There are no locations selected. You may save this coordination, but it won't be
                available for any PortCalls.
            </div>
        }
    </div>
);

const SelectedLocation = SuperCritical(({ location: l } : { location : LocationViewModel }) =>
    <div className="locations-selected-location">
        {/* Display locations in the same format used by our typeahead */}
        <span className="loc-name">{l.Model.Location() && LocationDisplayKey(l.Model.Location()!)}</span>
        <a href="" className="loc-delete fa fa-times" onClick={noDefault(l.Delete)} />
    </div>
);

const AllLocations = SuperCritical(({ onSelectLocations } : { onSelectLocations : () => void }) =>
    <div className="locations-input-shell">
        <span className="pc-breaker-label">All (default)</span>
        <a
            href=""
            className="locations-scope-toggle"
            onClick={noDefault(onSelectLocations)}
        >
            Select Locations
        </a>
    </div>
);

const Providers = SuperCritical(({ model: m } : { model : CoordinationConfigurator }) =>
    <div className="config-row providers-row">
        <label className="config-row-label">Providers</label>
        <div className="config-row-block is-not-berth-assignment">
            <div className="providers-table">
                <ProvidersTableHeader />
                <PCSortable
                    className="providers-table-body"
                    items={m.Providers}
                    onSort={m.OnSortProviders}
                    rowFactory={p =>
                        <SelectedProvider provider={p} key={p.Model.ProviderId()} />
                    }
                />
            </div>
            {!m.HasCurrentProvider() &&
                <div className="config-error">
                    At least one current provider is required.
                </div>
            }
            <div className="add-provider-shell">
                <TypeaheadInput
                    className="pc-breaker-label add-provider"
                    model={m.AddProvider.ProviderSelector}
                    placeholder="Add a provider..."
                />
            </div>
        </div>
    </div>
);

const ProvidersTableHeader = SuperCritical(() =>
    <div className="providers-table-header">
        <span className="providers-table-col col-name">Name</span>
        {/* TODO: Probably a col for NameForCoordinations too. */}
        <span className="providers-table-col col-editing-policy">Edit Mode</span>
        <span className="providers-table-col col-currency">Status</span>
        <span className="providers-table-col col-delete">&nbsp;</span>
    </div>
);

const SelectedProvider = SuperCritical(({ provider: p } : { provider : ProviderViewModel }) =>
    <li className="providers-table-row">
        <span className="providers-table-col col-name">{p.Model.Provider()?.Name()}</span>
        {/* TODO: Probably display the provider's NameForCoordinations here too. */}
        <span className="providers-table-col col-editing-policy">
            <select
                className="providers-select-pill"
                // NOTE: Setting up this <select> element to be specific to the AllowAgentStatusEdits bit w/in
                // the provider's EditingPolicy. If we add other bits that can be toggled on/off, we can either
                // add other <select> elements for those other bits or we can expand this <select> element to
                // cover more bits and include more logical combos in its list of <option>s.
                value={p.GetEditingPolicyBits(CoordinationEditingPolicy.AllowAgentStatusEdits)}
                onChange={e => p.SetEditingPolicyBits(CoordinationEditingPolicy.AllowAgentStatusEdits, +e.currentTarget.value)}
            >
                <option value={CoordinationEditingPolicy.AllowAgentStatusEdits}>Provider +</option>
                <option value={CoordinationEditingPolicy.None}>Provider Only</option>
            </select>
        </span>
        <span className="providers-table-col col-currency">
            <select
                className="providers-select-pill"
                value={p.Model.Currency()}
                onChange={e => p.Model.Currency(+e.currentTarget.value)}
                disabled={!p.IsPersisted}
            >
                {CoordinationProviderCurrencys.map(({ Id }) =>
                    <option value={Id} key={Id}>{CoordinationProviderCurrencyDisplayName[Id]}</option>
                )}
            </select>
        </span>
        <span className="providers-table-col col-delete">
            {!p.IsPersisted &&
                <a
                    href=""
                    className="delete-btn fa fa-times"
                    onClick={noDefault(p.Delete)}
                />
            }
        </span>
    </li>
);



// WEBPACK FOOTER //
// ./apps/portcall/coordinationconfigurator/view.tsx